// @ts-check
import React, { memo } from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core'; // eslint-disable-line no-unused-vars
import { useField, ErrorMessage } from 'formik';
// import TextareaAutosize, {
//   TextareaAutosizeProps, // eslint-disable-line no-unused-vars
// } from 'react-textarea-autosize';
import {
  VerticalField,
  HorizontalField,
} from 'preferral-ui/components/FieldStructure';
import Spinner from 'shared/components/Spinner';
import { IconContainer } from '../IconContainer';
import { SpinnerContainer } from '../SpinnerContainer';
import { FAIcon } from 'preferral-ui/components/FAIcon';

// inputProps?: Omit<TextareaAutosizeProps, "ref">;
/** @typedef {{
  value?: string;
  icon?: IconProp;
  isLoading?: boolean;
  className?: string;
  inputProps?: React.TextareaHTMLAttributes<HTMLTextAreaElement>;
  onChange?(newValue: string): void;
  onBlur?(e: any): void;
  onFocus?(e: any): void;
  minRows?: number;
  maxRows?: number;
}} InputTextAreaProps */

/** @type { React.FC<InputTextAreaProps> } */
export const InputTextArea = (props) => {
  const {
    value,
    onChange,
    onBlur,
    onFocus,
    icon,
    isLoading = false,
    inputProps = {},
    className = '',
    minRows = 3,
  } = props;

  /**
   * @param {React.ChangeEvent<HTMLTextAreaElement>} evt
   */
  function handleChange(evt) {
    if (onChange) onChange(evt.target.value);
  }

  /**
   * NB: `className` can be overridden by `inputProps`
   */

  return (
    <div className={!!icon ? 'has-icons-left' : ''}>
      <textarea
        value={value}
        onChange={handleChange}
        onBlur={onBlur}
        onFocus={onFocus}
        className={`form-input w-full ${className}`}
        rows={minRows}
        {...inputProps}
      />
      {icon && (
        <IconContainer className="text-gray-500">
          <FAIcon icon={icon} />
        </IconContainer>
      )}
      {isLoading && (
        <SpinnerContainer>
          <Spinner />
        </SpinnerContainer>
      )}
    </div>
  );
};

/**
 * TextAreaInput.
 */

/** @typedef {{
  name: string;
  id?: string;
  placeholder?: string;
  autoFocus?: boolean;
  icon?: IconProp;
  minRows?: number;
  maxRows?: number;
  className?: string;
  inputProps?: React.TextareaHTMLAttributes<HTMLTextAreaElement>;
}} TextAreaInputProps */

/** @type { React.FC<TextAreaInputProps> } */
export const TextAreaInput = memo((props) => {
  const {
    id,
    name,
    placeholder,
    autoFocus = false,
    icon,
    minRows,
    maxRows,
    className = '',
    inputProps = {},
  } = props;

  const [field, meta, helpers] = useField(name);
  const { value = '', onBlur } = field;
  const { setValue } = helpers;

  return (
    <>
      <InputTextArea
        inputProps={{
          id: id || name,
          placeholder,
          name,
          autoFocus,
          ...inputProps,
        }}
        icon={icon}
        className={
          meta && meta.touched && meta.error
            ? `${className} border border-red-500`
            : className
        }
        value={value}
        onChange={setValue}
        onBlur={onBlur}
        minRows={minRows}
        maxRows={maxRows}
      />
      <ErrorMessage
        component="p"
        name={name}
        className="mt-2 text-xs italic text-red-500"
      />
    </>
  );
});

/**
 * TextAreaField.
 */

/** @typedef {{
  label: string;
  indicateOptional?: boolean;
} & TextAreaInputProps} TextAreaFieldProps */

/** @type { React.FC<TextAreaFieldProps> } */
export const TextAreaField = (props) => {
  const { label, indicateOptional, ...rest } = props;

  return (
    <VerticalField
      id={`field--${rest.id || rest.name}`}
      htmlFor={rest.id || rest.name}
      label={label}
      indicateOptional={indicateOptional}
    >
      <TextAreaInput {...rest} placeholder={rest.placeholder || label} />
    </VerticalField>
  );
};

/** @type { React.FC<TextAreaFieldProps> } */
export const HorizontalTextAreaField = (props) => {
  const { label, indicateOptional, ...rest } = props;

  return (
    <HorizontalField
      id={`field--${rest.id || rest.name}`}
      htmlFor={rest.id || rest.name}
      label={label}
      indicateOptional={indicateOptional}
    >
      <TextAreaInput {...rest} placeholder={rest.placeholder || label} />
    </HorizontalField>
  );
};
